import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Globe, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { contactInfo, services } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // WhatsApp number (without + sign, just country code and number)
  const whatsappNumber = "919766110312"; // +91 9766110312

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Construct WhatsApp message
    const message = `*New Inquiry from Website*
    
*Name:* ${formData.name}
*Email:* ${formData.email}
*Phone:* ${formData.phone || 'Not provided'}
*Company:* ${formData.company || 'Not provided'}
*Service Interest:* ${formData.service || 'Not specified'}

*Message:*
${formData.message}`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // WhatsApp URL (works on both mobile and desktop)
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
    
    toast.success('Redirecting to WhatsApp... Please send the message to complete your inquiry.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      service: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  return (
    <div className="bg-[#0b0b0b]">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px),
                             repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px)`
          }} />
        </div>
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#b00000] via-red-600 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">Contact Us</span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              Let's Discuss Your Requirements
            </h1>
            <p className="text-base sm:text-lg text-white/70 leading-relaxed">
              Ready to partner with us? Get in touch to discuss how our 35+ years of experience 
              can support your Coal Handling Plant needs.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 sm:py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6 sm:gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-4 sm:space-y-6">
              {/* Office Address */}
              <div className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#b00000]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#b00000]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Registered Office</h3>
                    <div className="text-white/60 text-xs sm:text-sm leading-relaxed">
                      <p>{contactInfo.address.line1}</p>
                      <p>{contactInfo.address.line2}</p>
                      <p>{contactInfo.address.line3}</p>
                      <p>{contactInfo.address.city}, {contactInfo.address.state}</p>
                      <p>{contactInfo.address.country} - {contactInfo.address.pincode}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Persons */}
              {contactInfo.contacts.map((contact, index) => (
                <div key={index} className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#b00000]/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 sm:w-6 sm:h-6 text-[#b00000]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-sm sm:text-base">{contact.name}</h3>
                      <p className="text-[#b00000] text-xs sm:text-sm mb-1 sm:mb-2">{contact.designation}</p>
                      <div className="space-y-1">
                        {contact.phones.map((phone, idx) => (
                          <a key={idx} href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-white/60 text-xs sm:text-sm hover:text-white transition-colors">
                            <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-[#b00000]" />
                            {phone}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Email & Website */}
              <div className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-6">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#b00000]/20 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-[#b00000]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-0.5 sm:mb-1 text-sm sm:text-base">Email</h3>
                      <a href={`mailto:${contactInfo.email}`} className="text-white/60 text-xs sm:text-sm hover:text-white transition-colors break-all">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#b00000]/20 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-[#b00000]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-0.5 sm:mb-1 text-sm sm:text-base">Website</h3>
                      <a href={`https://${contactInfo.website}`} target="_blank" rel="noopener noreferrer" className="text-white/60 text-xs sm:text-sm hover:text-white transition-colors">
                        {contactInfo.website}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2">Send Us a Message</h2>
                <p className="text-white/60 mb-6 sm:mb-8 text-sm sm:text-base">Fill out the form below and our team will get back to you within 24 hours.</p>
                
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="name" className="text-white text-sm">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="bg-[#0b0b0b] border-white/20 text-white placeholder:text-white/40 rounded-none none h-10 sm:h-12 text-sm focus:border-[#b00000]"
                      />
                    </div>
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="email" className="text-white text-sm">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="bg-[#0b0b0b] border-white/20 text-white placeholder:text-white/40 rounded-none none h-10 sm:h-12 text-sm focus:border-[#b00000]"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="phone" className="text-white text-sm">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91-XXXXXXXXXX"
                        className="bg-[#0b0b0b] border-white/20 text-white placeholder:text-white/40 rounded-none none h-10 sm:h-12 text-sm focus:border-[#b00000]"
                      />
                    </div>
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="company" className="text-white text-sm">Company / Organization</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your organization"
                        className="bg-[#0b0b0b] border-white/20 text-white placeholder:text-white/40 rounded-none none h-10 sm:h-12 text-sm focus:border-[#b00000]"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="service" className="text-white text-sm">Service Interest</Label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full bg-[#0b0b0b] border border-white/20 text-white h-10 sm:h-12 px-3 text-sm focus:border-[#b00000] focus:outline-none"
                    >
                      <option value="" className="bg-[#0b0b0b]">Select a service</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.title} className="bg-[#0b0b0b]">
                          {service.title}
                        </option>
                      ))}
                      <option value="Other" className="bg-[#0b0b0b]">Other</option>
                    </select>
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="message" className="text-white text-sm">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your CHP requirements..."
                      rows={4}
                      className="bg-[#0b0b0b] border-white/20 text-white placeholder:text-white/40 rounded-none none resize-none text-sm focus:border-[#b00000]"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#FFD700] hover:bg-[#FFC000] text-black font-semibold rounded-none none px-6 sm:px-8 h-11 sm:h-14 text-sm sm:text-base transition-all duration-300 w-full sm:w-auto group"
                  >
                    {isSubmitting ? (
                      'Opening WhatsApp...'
                    ) : (
                      <>
                        Send via WhatsApp
                        <Send className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Options */}
      <section className="py-12 sm:py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-4">Quick Contact Options</h2>
            <p className="text-white/60 text-sm sm:text-base">Choose your preferred way to reach us</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <a href={`tel:${contactInfo.mobile.replace(/\s/g, '')}`} className="bg-[#0b0b0b] border border-white/10 p-6 sm:p-8 text-center hover:border-[#b00000]/50 transition-colors group">
              <Phone className="w-8 h-8 sm:w-10 sm:h-10 text-[#b00000] mx-auto mb-3 sm:mb-4" />
              <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Call Us</h3>
              <p className="text-[#b00000] font-medium text-sm sm:text-base">{contactInfo.mobile}</p>
              <p className="text-white/60 text-xs sm:text-sm mt-2">Speak directly with our team</p>
            </a>
            <a href={`mailto:${contactInfo.email}`} className="bg-[#0b0b0b] border border-white/10 p-6 sm:p-8 text-center hover:border-[#b00000]/50 transition-colors group">
              <Mail className="w-8 h-8 sm:w-10 sm:h-10 text-[#b00000] mx-auto mb-3 sm:mb-4" />
              <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Email Us</h3>
              <p className="text-[#b00000] font-medium text-xs sm:text-sm break-all">{contactInfo.email}</p>
              <p className="text-white/60 text-xs sm:text-sm mt-2">Send us your detailed inquiry</p>
            </a>
            <a href={`https://${contactInfo.website}`} target="_blank" rel="noopener noreferrer" className="bg-[#0b0b0b] border border-white/10 p-6 sm:p-8 text-center hover:border-[#b00000]/50 transition-colors group">
              <Globe className="w-8 h-8 sm:w-10 sm:h-10 text-[#b00000] mx-auto mb-3 sm:mb-4" />
              <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Visit Website</h3>
              <p className="text-[#b00000] font-medium text-sm sm:text-base">{contactInfo.website}</p>
              <p className="text-white/60 text-xs sm:text-sm mt-2">Learn more about our services</p>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
